// Type definitions for competencies data structure
// These types match the structure in public/competencies-data.json

export enum FrameworkLevel {
  LearnerOutcome = "learnerOutcome",
  OutcomeArea = "outcomeArea",
  Competency = "competency",
  SubCompetency = "subCompetency",
}

type ImageReference = {
  sourceUrl: string;
};

export type ApiLearnerOutcome = {
  id: string;
  name: string;
  icon: ImageReference;
  iconAlt: ImageReference;
  description: string;
  poster: ImageReference | null;
  learnerOutcomeCard: ImageReference;
  learnerOutcomeAltCard: ImageReference;
  outcomeAreas: ApiOutcomeArea[];
};

export type ApiOutcomeArea = {
  id: string;
  name: string;
  competencies: ApiCompetency[];
};

export type ApiCompetency = {
  id: string;
  name: string;
  descriptionShort: string;
  descriptionLong: string;
  descriptionRtf: string;
  catchphraseHeadline: string | null;
  catchphraseDescription: string | null;
  definitionHeadline: string | null;
  definitionDescription: string;
  competencyCard: ImageReference;
  icon: ImageReference;
  namedIcon: ImageReference;
  externalResourcesUrl: string | null;
  subCompetencies: SubCompetency[];
};

export type ApiSubCompetency = {
  id: string;
  name: string;
  description: string | null;
  performanceLevel1Id: string;
  performanceLevel1Description: string;
  performanceLevel2Id: string;
  performanceLevel2Description: string;
  performanceLevel3Id: string;
  performanceLevel3Description: string;
  performanceLevel4Id: string;
  performanceLevel4Description: string;
  externalResourcesUrl: string | null;
  activities: Activity[];
};

export type Activity = {
  urlSlug: string;
  name: string;
  embedUrl: string;
  slidesUrl: string;
  overview: string;
  context: string | null;
  actions: string | null;
  runTime: string;
  materials: string;
  preparation: string;
  activityCard: ImageReference;
  learnerOutcomeId: string;
  competencyId: string;
  competencyName: string;
  subCompetencyId: string;
  subCompetencyName: string;
  featured: boolean | null;
  featuredOrder: number | null;
  featuredImage1: ImageReference | null;
  featuredImage2: ImageReference | null;
  featuredImage3: ImageReference | null;
  featuredImage4: ImageReference | null;
};

type Normalized<T, U extends keyof T> = Omit<T, U> & {
  [key in U]: string[];
};

export type LearnerOutcome = Normalized<ApiLearnerOutcome, "outcomeAreas">;
export type OutcomeArea = Normalized<ApiOutcomeArea, "competencies">;
export type Competency = Normalized<ApiCompetency, "subCompetencies">;
export type SubCompetency = ApiSubCompetency;

export const isLearnerOutcome = (
  entity: LearnerOutcome | OutcomeArea | Competency | SubCompetency,
): entity is LearnerOutcome => "outcomeAreas" in entity;

export const isCompetency = (
  entity: LearnerOutcome | OutcomeArea | Competency | SubCompetency,
): entity is Competency => "subCompetencies" in entity;
